import React from 'react'
import { Stack, Typography } from '@mui/material'
import AiCard, { AiCardDivider } from './cardKit/AiCard'
import DataRow from './cardKit/DataRow'
import NoticeStrip from './cardKit/NoticeStrip'
import { formatInquiryRupee, type PnlInquiry, type PnlRow } from './domains/tradingDomain'

type Props = {
    inquiry: PnlInquiry
}

/** Signed rupee text plus the platform's P&L colour class. */
function signed(value: number | null | undefined): { text: string; className: string } | null {
    if (value == null || Number.isNaN(value)) return null
    const formatted = formatInquiryRupee(Math.abs(value)) ?? ''
    if (value > 0) return { text: `+${formatted}`, className: 'green-text' }
    if (value < 0) return { text: `−${formatted}`, className: 'red-text' }
    return { text: formatted, className: '' }
}

function rowTotal(row: PnlRow): number {
    return (row.realisedPnl ?? 0) + (row.unrealisedPnl ?? 0)
}

export default function PnlInquiryCard({ inquiry }: Props) {
    if (inquiry.outcome !== 'MATCHED' || inquiry.rows.length === 0) {
        return null
    }
    const total = signed(inquiry.totalPnl)
    const realised = signed(inquiry.totalRealisedPnl)
    const unrealised = signed(inquiry.totalUnrealisedPnl)
    return (
        <AiCard accent="neutral">
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>
                    Today&apos;s P&amp;L
                </Typography>
                {total ? (
                    <Typography variant="h6" fontWeight={700} className={`font-feature-num ${total.className}`}>
                        {total.text}
                    </Typography>
                ) : null}
            </Stack>
            {realised ? <DataRow label="Realised" value={realised.text} mono valueColor={undefined} /> : null}
            {unrealised ? <DataRow label="Unrealised" value={unrealised.text} mono /> : null}
            {inquiry.rows.length > 1 ? (
                <>
                    <AiCardDivider />
                    {inquiry.rows.slice(0, 8).map((row) => {
                        const value = signed(rowTotal(row))
                        return (
                            <Stack
                                key={`${row.exchange}-${row.securityCode}-${row.product}`}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="baseline"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                            >
                                <Typography variant="body2" sx={{ minWidth: 0 }}>
                                    {row.instrument.label}
                                </Typography>
                                {value ? (
                                    <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        className={`font-feature-num ${value.className}`}
                                    >
                                        {value.text}
                                    </Typography>
                                ) : null}
                            </Stack>
                        )
                    })}
                    {inquiry.rows.length > 8 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            +{inquiry.rows.length - 8} more
                        </Typography>
                    ) : null}
                </>
            ) : null}
            {inquiry.livePriceMissing ? (
                <NoticeStrip
                    notices={[
                        {
                            text: 'Some open positions had no live price, so unrealised P&L is incomplete.',
                            severity: 'info',
                        },
                    ]}
                />
            ) : null}
        </AiCard>
    )
}
